import {
  h, ref,
} from "vue";
import type {
  Locale,
} from "~/stores/locale";
import AbstractDialogTitle from "~/components/abstract-dialog/title.vue";
import TermsAndConditions from "~/components/TermsAndConditions.vue";
import PrivacyPolicy from "~/components/PrivacyPolicy.vue";
import DialogFooter from "~/services/agreement-dialog/footer.vue";
import {
  useDialogStoreV2,
} from "~/components/abstract-dialog/useDialogStoreV2";
import {
  AGREEMENT_TYPE,
} from "~/types/agreement";
import type {
  AgreementType,
} from "~/types/agreement";

type AgreementDialogOptions = {
  readOnly?: boolean;
};

export function useAgreementDialog(
  type: AgreementType,
  initialLanguage: Locale,
  options: AgreementDialogOptions = {},
) {
  const dialogStoreV2 = useDialogStoreV2();
  const {$t} = useNuxtApp();

  const resolution = defer<{success: boolean; version: string; language: Locale}>();
  const selectedLanguage = ref<Locale>(initialLanguage);

  const loadedVersion = ref<string>("");
  const loadedLanguage = ref<Locale>(initialLanguage);

  const titleText = type === AGREEMENT_TYPE.TERMS_AND_CONDITIONS
    ? $t("REGISTER.AGREE_TERMS_LINK")
    : $t("REGISTER.AGREE_PRIVACY_LINK");

  const componentToRender = type === AGREEMENT_TYPE.TERMS_AND_CONDITIONS ? TermsAndConditions : PrivacyPolicy;

  const {hideDialog} = dialogStoreV2.showDialog({
    titleComponent: h(AbstractDialogTitle, {
      title: titleText,
    }),
    bodyComponent: () => h(componentToRender, {
      "modelValue": selectedLanguage.value,
      "onUpdate:modelValue": (val: Locale) => {
        selectedLanguage.value = val;
      },
      "onLoaded": (data: {version: string; language: Locale}) => {
        loadedVersion.value = data.version;
        loadedLanguage.value = data.language;
      },
    }),
    footerComponent: options.readOnly
      ? null
      : () => h(DialogFooter, {
          onConfirm: () => {
            resolution.resolve({
              success: true,
              version: loadedVersion.value,
              language: loadedLanguage.value,
            });
            hideDialog();
          },
          onDecline: () => {
            resolution.resolve({success: false, version: "", language: initialLanguage});
            hideDialog();
          },
        }),
    options: {
      dialogClass: "!w-[95%] !max-w-[95%] sm:!max-w-[800px]",
      dialogMinWidth: "",
      dialogMinHeight: "",
      onClose: () => {
        resolution.resolve({success: false, version: "", language: initialLanguage});
      },
      allowCloseByEscape: true,
    },
  });

  return resolution.promise!;
}
